import type { VoiceOption } from '@/constants/voiceOptions';
import { voiceOptions } from '@/constants/voiceOptions';
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import useScreenDimensions from '@/hooks/useScreenDimensions';
import type { CampaignWorkflow } from '@/types/campaign';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import type { SelectChangeEvent } from '@mui/material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { useNavigate } from 'raviger';
import React, { useEffect, useMemo, useState } from 'react';

interface OutgoingProps {
  theme: 'dark' | 'light';
  campaign: CampaignWorkflow;
}

const colors: Record<OutgoingProps['theme'], Record<string, string>> = {
  dark: {
    paperBg: '#202020',
    paperBorder: 'rgba(255,255,255,0.1)',
    callButton: '#555555',
    callButtonHover: '#666666',
    textPrimary: '#c8c8c8',
    textSecondary: '#a0a0a0',
    buttonText: '#ffffff',
    statusChipActive: '#2e7d32',
    statusChipNew: '#1976d2',
    statusChipContacted: '#ed6c02',
    statusChipInactive: '#9e9e9e',
    inputBg: '#333333',
    inputBorder: 'rgba(255,255,255,0.2)',
  },
  light: {
    paperBg: '#f9f9f9',
    paperBorder: 'rgba(0,0,0,0.05)',
    callButton: '#eaeaea',
    callButtonHover: '#d5d5d5',
    textPrimary: '#38383b',
    textSecondary: '#6c6c6c',
    buttonText: '#333333',
    statusChipActive: '#4caf50',
    statusChipNew: '#2196f3',
    statusChipContacted: '#ff9800',
    statusChipInactive: '#bdbdbd',
    inputBg: '#ffffff',
    inputBorder: 'rgba(0,0,0,0.2)',
  },
};

function OutgoingTab({ theme, campaign }: OutgoingProps): React.ReactNode {
  const navigate = useNavigate();
  // Get leads from store
  const { leads, fields, leadSets, activeLeadSetId, selectedLeads } =
    useAlchemystStoreForAi((store) => ({
      leads: store.leads,
      fields: store.fields,
      leadSets: store.leadSets,
      activeLeadSetId: store.activeLeadSetId,
      selectedLeads: store.selectedLeads,
    }));

  // Get the active lead set or use the legacy leads/fields if no active set
  const activeLeadSet = activeLeadSetId ? leadSets[activeLeadSetId] : null;
  const currentLeads = selectedLeads;
  const currentFields = activeLeadSet ? activeLeadSet.fields : fields;
  const [calling, setCalling] = useState(false);

  const [selectedVoice, setSelectedVoice] = useState<VoiceOption>(
    voiceOptions[0]
  );

  const handleChange = (event: SelectChangeEvent) => {
    const selected = voiceOptions.find((v) => v.value === event.target.value);
    if (selected) {
      setSelectedVoice(selected);
    }
  };

  // Context form state
  const [contextData, setContextData] = useState({
    fallback: '',
    context: '',
    instructions: '',
  });

  // Selected phone numbers state - initialize with first two numbers if available
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>([]);
  const [phoneNumberOptions, setPhoneNumberOptions] = useState<string[]>([]);

  const { height, width: screenWidth } = useScreenDimensions();

  useEffect(() => {
    const fetchTwilioContacts = async () => {
      const res = await fetchWithRewrites(
        `/api/v1/persona/maya/integrations/twilio/fetch`
      );

      const data = await res.json();

      setPhoneNumberOptions(
        data.data.map(
          (item: { credentials: { phoneNumber: string } }) =>
            item.credentials.phoneNumber
        )
      );
    };

    fetchTwilioContacts();
  }, []);

  // Prepare rows with IDs for DataGrid
  const rows = useMemo(() => {
    return currentLeads.map((lead, index) => {
      // Ensure all required fields exist with defaults
      const processedLead = {
        ...lead, // First spread the original lead
        id: (index + 1).toString(),
        status: lead.status || 'New',
      };

      return processedLead;
    });
  }, [currentLeads]);

  // Define our required fields - we'll always show these regardless of what's in currentFields
  const requiredFields = Object.keys(leads.length > 0 ? leads[0] : {});

  // Define columns for the DataGrid based on specific fields we want to show
  const columns: GridColDef[] = useMemo(() => {
    if (leads.length === 0) return [];

    // Set a fixed width for each column type using rem values
    const columnWidths: Record<string, number> = {
      name: 8, // 128px
      first_name: 7, // 112px
      last_name: 7, // 112px
      email: 12, // 192px
      email_status: 6, // 96px
      title: 9, // 144px
      company: 8, // 128px
      industry: 8, // 128px
      phones: 8, // 128px
      location: 7, // 112px
      profile_pic: 6, // 96px
      linkedin_url: 12, // 192px
      phone_numbers: 9, // 144px
    };

    const baseColumns: GridColDef[] = [
      {
        field: 'id',
        headerName: 'ID',
        width: 1, // 4rem = 64px
        filterable: false,
      },
    ];

    const requiredFieldsWithoutId = requiredFields.filter(
      (field) => field.toLowerCase() !== 'id'
    );

    const fieldMap: Record<string, { header: string; width: number }> = {
      ...Object.fromEntries(
        requiredFieldsWithoutId.map((fieldName: string) => [
          fieldName,
          {
            header:
              fieldName[0].toUpperCase() +
              fieldName.slice(1).replace(/_/g, ' '),
            width: (columnWidths[fieldName] || 7) * 16, // Convert rem to pixels, default to 7rem
          },
        ])
      ),
    };

    // Ensure all required fields are included, even if they're not in currentFields
    const fieldColumns: GridColDef[] = requiredFieldsWithoutId.map(
      (field: string) => ({
        field,
        headerName: fieldMap[field].header,
        width: fieldMap[field].width,
        type: 'string',
        filterable: true,
        renderCell: (params: any) => {
          // Add safe rendering that handles missing data
          return params.value || '—';
        },
      })
    );

    // Add custom status field with chip rendering
    const statusColumn: GridColDef = {
      field: 'status',
      headerName: 'Status',
      width: 7.5 * 16, // 7.5rem = 120px
      filterable: true,
      renderCell: (params) => {
        if (!params.value) return '—';

        let chipColor;
        switch (params.value) {
          case 'Active':
            chipColor = colors[theme].statusChipActive;
            break;
          case 'New':
            chipColor = colors[theme].statusChipNew;
            break;
          case 'Contacted':
            chipColor = colors[theme].statusChipContacted;
            break;
          case 'Inactive':
          default:
            chipColor = colors[theme].statusChipInactive;
            break;
        }

        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              backgroundColor: chipColor,
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        );
      },
    };

    // Always include the status column
    return [...baseColumns, ...fieldColumns, statusColumn];
  }, [theme, leads, requiredFields]);

  // Handle context data changes
  const handleContextChange = (
    field: keyof typeof contextData,
    value: string | string[]
  ) => {
    setContextData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle phone number selection
  const handlePhoneNumberChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedNumbers(typeof value === 'string' ? value.split(',') : value);
  };

  async function handleMakeCallWithSelectedLeads() {
    setCalling(true);
    const campaignInfo = {
      id: 'new',
      name: campaign.name,
      description: campaign.description,
      locations: campaign.locations,
      active: true,
      status: 'running',
      leads: selectedLeads,
      channels: {
        emails: [],
        whatsapp: [],
        telegram: [],
        voice: phoneNumberOptions,
      },
      steps: [],
      //TODO remove hardcoded location parameter or make it optional in backend
      stepConfig: [
        {
          id: 0,
          message: {
            type: 'social',
            subject: '',
            body: '',
          },
          from: [],
          waitTime: 1,
          aiGenerated: false,
          actions: ['Voice'],
          name: '',
          useAi: {
            create: false,
            modify: false,
          },
          voice: {
            context: {
              information: contextData.context,
              instructions: contextData.instructions,
            },
            settings: {
              voiceName: selectedVoice.value,
              voiceProvider: 'eleven_labs',
              voiceId: selectedVoice.providerId,
            },
            fallback: contextData.fallback,
          },
        },
      ],
      metadata: {
        actions: ['Voice'],
      },
    };

    try {
      // Creating default campaign
      const res = await fetchWithRewrites('/api/v1/campaigns/update', {
        method: 'POST',
        body: JSON.stringify({ ...campaignInfo }),
      });

      if (!res.ok) {
        console.error('Failed to create campaign:', await res.text());
        return;
      }

      const data = await res.json();

      // Updating campaign with new ID
      const runningRes = await fetchWithRewrites('/api/v1/campaigns/update', {
        method: 'POST',
        body: JSON.stringify({
          ...campaignInfo,
          _id: data.updatedId,
          id: data.updatedId,
        }),
      });

      setTimeout(() => {
        navigate('/campaigns');
      }, 15000);

      if (!runningRes.ok) {
        console.error('Failed to update campaign:', await runningRes.text());
      }
    } catch (error) {
      console.error('Error during campaign creation/update:', error);
    } finally {
      setCalling(false);
    }
  }

  return (
    <Stack
      spacing={2}
      sx={{
        display: 'flex',
        width: '100%',
        maxWidth: '100vw',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Leads Table */}
      <Paper
        elevation={2}
        sx={{
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          minHeight: '18.75rem',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          background: colors[theme].paperBg,
          border: '1px solid',
          borderColor: colors[theme].paperBorder,
          boxShadow:
            theme === 'dark'
              ? '0 0.125rem 0.5rem rgba(0, 0, 0, 0.15)'
              : '0 0.125rem 0.5rem rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            justifyContent: 'flex-start',
            alignItems: 'baseline',
            m: 2,
          }}
        >
          <TextField
            autoFocus
            disabled
            fullWidth
            label="Campaign Name"
            placeholder="Campaign Name"
            size="small"
            sx={{
              backgroundColor: 'background.default',
            }}
            value={campaign.name}
            variant="outlined"
          />
          <TextField
            disabled
            fullWidth
            label="Campaign Description"
            placeholder="Campaign Description"
            size="small"
            sx={{
              backgroundColor: 'background.default',
            }}
            value={campaign.description}
            variant="outlined"
          />
          <Autocomplete
            disabled
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option) => option === option}
            multiple
            options={[]}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Locations"
                margin="normal"
                placeholder="Select Locations"
                variant="outlined"
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  color="primary"
                  label={option}
                  variant="outlined"
                  {...getTagProps({ index })}
                  key={option} // use unique key
                />
              ))
            }
            sx={{ width: '100%' }}
            value={campaign.locations}
          />
        </Box>
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography color={colors[theme].textPrimary} variant="h6">
            Leads Table
          </Typography>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            px: 2,
            pb: 2,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              overflow: 'hidden',
              '& .MuiDataGrid-root': {
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden !important',
              },
            }}
          >
            <DataGridPro
              columns={columns}
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                    page: 0,
                  },
                },
                filter: {
                  filterModel: {
                    items: [],
                    quickFilterValues: [],
                  },
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              pagination
              rows={rows}
              showCellVerticalBorder
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 300 },
                },
              }}
              slots={{ toolbar: GridToolbar }}
              sx={{
                width: '100%',
                maxWidth: '100%',
                '& .MuiDataGrid-root': {
                  maxWidth: '100%',
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'auto !important',
                  maxWidth: '100%',
                },
                '& .MuiDataGrid-main': {
                  overflow: 'auto !important',
                  maxWidth: '100%',
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  maxWidth: '100%',
                },
                '& .MuiDataGrid-virtualScrollerRenderZone': {
                  maxWidth: '100%',
                },
                '& .MuiDataGrid-footerContainer': {
                  maxWidth: '100%',
                },
                overflowX: 'auto',
                overflowY: 'auto',
                maxHeight: '35vh',
                backgroundColor: theme === 'dark' ? '#1a1a1a' : '#fff',
                borderColor: colors[theme].paperBorder,
                color: colors[theme].textPrimary,
                '& .MuiDataGrid-cell': {
                  borderColor: colors[theme].paperBorder,
                  color: colors[theme].textPrimary,
                  fontSize: '0.85rem',
                  padding: '0.25rem 0.5rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme === 'dark' ? '#333' : '#f5f5f5',
                  color: colors[theme].textPrimary,
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: `2px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  maxWidth: '100%',
                },
                '& .MuiDataGrid-row': {
                  maxWidth: '100%',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor:
                    theme === 'dark'
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(0,0,0,0.04)',
                },
                '& .MuiDataGrid-filterForm': {
                  backgroundColor: theme === 'dark' ? '#282828' : '#f8f8f8',
                },
                '& .MuiDataGrid-toolbarContainer': {
                  backgroundColor: theme === 'dark' ? '#242424' : '#f5f5f5',
                  borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  padding: '4px 8px',
                },
                '& .MuiDataGrid-toolbarQuickFilter': {
                  flex: 1,
                  '& .MuiInputBase-root': {
                    backgroundColor: theme === 'dark' ? '#333' : '#fff',
                    color: colors[theme].textPrimary,
                    borderRadius: '4px',
                    '& .MuiInputBase-input': {
                      padding: '4px 8px',
                    },
                  },
                },
                '& .MuiTablePagination-root': {
                  color: colors[theme].textPrimary,
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                  {
                    color: colors[theme].textSecondary,
                  },
                '& .MuiTablePagination-select': {
                  color: colors[theme].textPrimary,
                },
                '& .MuiIconButton-root.Mui-disabled': {
                  color:
                    theme === 'dark'
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(0,0,0,0.3)',
                },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Context Form */}
      <Paper
        elevation={2}
        sx={{
          width: '100%',
          p: 2,
          minHeight: 200,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          background: colors[theme].paperBg,
          border: '1px solid',
          borderColor: colors[theme].paperBorder,
          boxShadow:
            theme === 'dark'
              ? '0 2px 8px rgba(0, 0, 0, 0.15)'
              : '0 2px 8px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Typography
          color={colors[theme].textPrimary}
          sx={{ mb: 2, textAlign: 'center' }}
          variant="h6"
        >
          Campaign Information
        </Typography>

        <Box sx={{ px: 1 }}>
          <Typography
            color={colors[theme].textPrimary}
            sx={{ mb: 1, fontWeight: 'bold' }}
            variant="subtitle2"
          >
            Default Fallback
          </Typography>
          <TextField
            fullWidth
            multiline
            onChange={(e) => {
              handleContextChange('fallback', e.target.value);
            }}
            placeholder="Enter your default fallback..."
            rows={2}
            size="small"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors[theme].inputBg,
                '& fieldset': {
                  borderColor: colors[theme].inputBorder,
                },
              },
              '& .MuiInputBase-input': {
                color: colors[theme].textPrimary,
              },
            }}
            value={contextData.fallback}
          />

          <Typography
            color={colors[theme].textPrimary}
            sx={{ mb: 1, fontWeight: 'bold' }}
            variant="subtitle2"
          >
            Context
          </Typography>
          <TextField
            fullWidth
            multiline
            onChange={(e) => {
              handleContextChange('context', e.target.value);
            }}
            placeholder="Enter context here..."
            rows={3}
            size="small"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors[theme].inputBg,
                '& fieldset': {
                  borderColor: colors[theme].inputBorder,
                },
              },
              '& .MuiInputBase-input': {
                color: colors[theme].textPrimary,
              },
            }}
            value={contextData.context}
          />

          <Typography
            color={colors[theme].textPrimary}
            sx={{ mb: 1, fontWeight: 'bold' }}
            variant="subtitle2"
          >
            Instructions
          </Typography>
          <TextField
            fullWidth
            multiline
            onChange={(e) => {
              handleContextChange('instructions', e.target.value);
            }}
            placeholder="Enter your instructions..."
            rows={3}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors[theme].inputBg,
                '& fieldset': {
                  borderColor: colors[theme].inputBorder,
                },
              },
              '& .MuiInputBase-input': {
                color: colors[theme].textPrimary,
              },
            }}
            value={contextData.instructions}
          />
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Select
            onChange={handleChange}
            sx={{
              width: '100%',
              marginBottom: '0.5rem',
              borderRadius: '0.5rem',
            }}
            value={selectedVoice.value}
          >
            {voiceOptions.map((voice) => (
              <MenuItem key={voice.value} value={voice.value}>
                {voice.label}
              </MenuItem>
            ))}
          </Select>
          <Button
            fullWidth
            startIcon={
              calling ? <CircularProgress size={20} color="inherit" /> : null
            }
            disabled={calling}
            onClick={handleMakeCallWithSelectedLeads}
            sx={{
              py: 1.5,
              borderRadius: 2,
              backgroundColor: colors[theme].callButton,
              color: colors[theme].buttonText,
              fontWeight: 'bold',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: colors[theme].callButtonHover,
              },
              boxShadow:
                theme === 'dark'
                  ? '0 2px 4px rgba(0, 0, 0, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
            variant="contained"
          >
            CALL
          </Button>
        </Box>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: 2,
            background: colors[theme].paperBg,
            borderColor: colors[theme].paperBorder,
            flex: 3,
            display: 'flex',
            flexDirection: 'column',
            boxShadow:
              theme === 'dark'
                ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                : '0 2px 8px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Typography
            color={colors[theme].textPrimary}
            sx={{ mb: 1 }}
            variant="subtitle2"
          >
            Selected Phone Numbers
          </Typography>

          {/* Display selected numbers as chips outside the dropdown */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {selectedNumbers.map((value) => {
              const number = phoneNumberOptions.find(
                (phone) => phone === value
              );
              return number ? (
                <Chip
                  key={value}
                  label={number}
                  onDelete={() => {
                    setSelectedNumbers((prev) =>
                      prev.filter((selectedId) => selectedId !== value)
                    );
                  }}
                  size="small"
                  sx={{
                    bgcolor: theme === 'dark' ? '#444444' : '#f0f0f0',
                    color: colors[theme].textPrimary,
                    fontWeight: 'medium',
                    '& .MuiChip-deleteIcon': {
                      color: theme === 'dark' ? '#888888' : '#666666',
                      '&:hover': {
                        color: theme === 'dark' ? '#bbbbbb' : '#333333',
                      },
                    },
                  }}
                />
              ) : null;
            })}
          </Box>

          <FormControl sx={{ width: '100%' }}>
            <Select
              displayEmpty
              input={<OutlinedInput />}
              multiple
              onChange={handlePhoneNumberChange}
              renderValue={() => (
                <Typography color={colors[theme].textSecondary} variant="body2">
                  {selectedNumbers.length > 0
                    ? `${selectedNumbers.length} number(s) selected`
                    : 'Select phone numbers...'}
                </Typography>
              )}
              size="small"
              sx={{
                backgroundColor: colors[theme].inputBg,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors[theme].inputBorder,
                },
                '& .MuiSelect-select': {
                  color: colors[theme].textPrimary,
                },
              }}
              value={selectedNumbers}
            >
              {phoneNumberOptions.map((phone, index) => (
                <MenuItem key={index} value={phone}>
                  <Checkbox checked={selectedNumbers.includes(phone)} />
                  <ListItemText primary={phone} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography
            color={colors[theme].textSecondary}
            sx={{ mt: 1, fontSize: '0.75rem' }}
            variant="body2"
          >
            {selectedNumbers.length} number(s) selected for calling
          </Typography>
        </Paper>
      </Box>
    </Stack>
  );
}

export default OutgoingTab;
